import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Close from '@mui/icons-material/Close';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import ThumbUpAltOutlined from '@mui/icons-material/ThumbUpAltOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import { useAuth } from '../../context/AuthContext';
import {
  countOtherBlockingModals,
  hasSeenWhatsNewEpic4,
  markWhatsNewEpic4Seen,
  shouldShowWhatsNewEpic4,
  WHATS_NEW_EPIC4_ARTICLE_URL,
  WHATS_NEW_EPIC4_SLIDE_IDS,
  type WhatsNewEpic4SlideId,
} from '../../constants/whatsNewEpic4';
import '../WhatsNewTour/WhatsNewTour.css';

const SHOW_DELAY_MS = 700;
const BLOCKING_MODAL_POLL_MS = 400;
/** Au-dessus du footer mobile (.mobile-footer z-index 1002). */
const WHATS_NEW_Z_INDEX = 1200;

const SLIDE_ICONS: Record<WhatsNewEpic4SlideId, React.ElementType> = {
  intro: AutoAwesomeOutlined,
  booking: EventAvailableOutlined,
  followUp: MenuBookOutlined,
  trust: ThumbUpAltOutlined,
  roadmap: TrendingUpOutlined,
};

export const WhatsNewEpic4Modal: React.FC = () => {
  const { t } = useTranslation('whatsNew');
  const { user } = useAuth();
  const location = useLocation();
  const titleId = useId();
  const hostRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const proId = user?.pro_id;

  const dismiss = useCallback(() => {
    if (proId) {
      markWhatsNewEpic4Seen(proId);
    }
    setOpen(false);
  }, [proId]);

  const openArticle = useCallback(() => {
    window.open(WHATS_NEW_EPIC4_ARTICLE_URL, '_blank', 'noopener,noreferrer');
    dismiss();
  }, [dismiss]);

  useEffect(() => {
    if (open || !proId || hasSeenWhatsNewEpic4(proId)) return undefined;

    let cancelled = false;
    let delayTimer: number | undefined;
    let pollTimer: number | undefined;

    const tryScheduleOpen = () => {
      if (cancelled || hasSeenWhatsNewEpic4(proId)) return;

      const pathEligible = shouldShowWhatsNewEpic4({
        proId,
        alreadySeen: false,
        pathname: location.pathname,
        accountLimited: Boolean(user?.account_restriction?.is_limited),
        otherBlockingModals: 0,
      });
      if (!pathEligible) return;

      if (countOtherBlockingModals(hostRef.current) > 0) {
        pollTimer = window.setTimeout(tryScheduleOpen, BLOCKING_MODAL_POLL_MS);
        return;
      }

      delayTimer = window.setTimeout(() => {
        if (cancelled || hasSeenWhatsNewEpic4(proId)) return;
        if (countOtherBlockingModals(hostRef.current) > 0) {
          pollTimer = window.setTimeout(tryScheduleOpen, BLOCKING_MODAL_POLL_MS);
          return;
        }
        setSlideIndex(0);
        setOpen(true);
      }, SHOW_DELAY_MS);
    };

    tryScheduleOpen();

    return () => {
      cancelled = true;
      if (delayTimer !== undefined) window.clearTimeout(delayTimer);
      if (pollTimer !== undefined) window.clearTimeout(pollTimer);
    };
  }, [
    open,
    proId,
    location.pathname,
    user?.account_restriction?.is_limited,
  ]);

  const lastIndex = WHATS_NEW_EPIC4_SLIDE_IDS.length - 1;
  const slideId = WHATS_NEW_EPIC4_SLIDE_IDS[slideIndex] ?? 'intro';
  const isLast = slideIndex >= lastIndex;
  const Icon = SLIDE_ICONS[slideId];

  const goNext = useCallback(() => {
    setSlideIndex((current) => Math.min(current + 1, lastIndex));
  }, [lastIndex]);

  const goBack = useCallback(() => {
    setSlideIndex((current) => Math.max(current - 1, 0));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') goNext();
      if (event.key === 'ArrowLeft') goBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, goNext, goBack]);

  const slideTitle =
    slideId === 'intro'
      ? t('title')
      : slideId === 'roadmap'
        ? t('roadmapTitle')
        : t(`items.${slideId}.title`);
  const slideBody =
    slideId === 'intro'
      ? t('intro')
      : slideId === 'roadmap'
        ? t('roadmapBox')
        : t(`items.${slideId}.body`);

  const dots = useMemo(
    () =>
      WHATS_NEW_EPIC4_SLIDE_IDS.map((id, index) => (
        <button
          key={id}
          type="button"
          className={
            index === slideIndex
              ? 'whats-new-tour__dot whats-new-tour__dot--active'
              : 'whats-new-tour__dot'
          }
          aria-label={t('step', {
            current: index + 1,
            total: WHATS_NEW_EPIC4_SLIDE_IDS.length,
          })}
          aria-current={index === slideIndex ? 'step' : undefined}
          onClick={() => setSlideIndex(index)}
        />
      )),
    [slideIndex, t]
  );

  return (
    <div ref={hostRef} className="whats-new-epic4-host">
      {!open ? null : (
    <Dialog
      open={open}
      onClose={dismiss}
      maxWidth="sm"
      fullWidth
      className="whats-new-tour"
      aria-labelledby={titleId}
      sx={{ zIndex: WHATS_NEW_Z_INDEX }}
      PaperProps={{
        className: 'whats-new-tour__paper',
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          maxWidth: 480,
          margin: 2,
          display: 'flex',
          flexDirection: 'column',
          maxHeight:
            'min(calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 80px), 720px)',
        },
      }}
    >
      <div className="whats-new-tour__shell">
        <IconButton
          className="whats-new-tour__close"
          onClick={dismiss}
          aria-label={t('close')}
          size="small"
          disableRipple
        >
          <Close fontSize="small" aria-hidden />
        </IconButton>

        <div className="whats-new-tour__icon" aria-hidden>
          <Icon />
        </div>
        <p className="whats-new-tour__kicker">{t(`kickers.${slideId}`)}</p>
        <h2 id={titleId} className="whats-new-tour__title">
          {slideTitle}
        </h2>
        <p className="whats-new-tour__body">{slideBody}</p>

        <div className="whats-new-tour__dots" role="tablist">
          {dots}
        </div>

        <div className="whats-new-tour__actions">
          {slideIndex > 0 ? (
            <button type="button" className="whats-new-tour__secondary" onClick={goBack}>
              {t('back')}
            </button>
          ) : (
            <button type="button" className="whats-new-tour__secondary" onClick={dismiss}>
              {t('later')}
            </button>
          )}
          {isLast ? (
            <button type="button" className="whats-new-tour__primary" onClick={openArticle}>
              {t('cta')}
            </button>
          ) : (
            <button type="button" className="whats-new-tour__primary" onClick={goNext}>
              {t('next')}
            </button>
          )}
        </div>
      </div>
    </Dialog>
      )}
    </div>
  );
};

export default WhatsNewEpic4Modal;
